import React, { useState, useEffect, useRef } from 'react';
import type { Task, TaskType } from '../../types';

interface TaskSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  taskTypes: TaskType[];
  onTaskSelect?: (task: Task) => void;
}

export const TaskSearchModal: React.FC<TaskSearchModalProps> = ({
  isOpen,
  onClose,
  tasks,
  taskTypes,
  onTaskSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultRefs = useRef<(HTMLDivElement | null)[]>([]);

  // 获取任务类型
  const getTaskType = (taskTypeId: string) => {
    return taskTypes.find(type => type.id === taskTypeId);
  };

  // 过滤任务
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTasks([]);
      setSelectedIndex(0);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = tasks.filter(task => {
      const taskType = getTaskType(task.taskTypeId);
      return (
        task.title.toLowerCase().includes(term) ||
        (task.description || '').toLowerCase().includes(term) ||
        (taskType?.name || '').toLowerCase().includes(term) ||
        (task.user?.username || '').toLowerCase().includes(term)
      );
    }).slice(0, 10); // 限制显示前10个结果

    setFilteredTasks(filtered);
    setSelectedIndex(0);
  }, [searchTerm, tasks, taskTypes]);

  // 键盘导航
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredTasks.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredTasks[selectedIndex]) {
          handleTaskSelect(filteredTasks[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  // 选择任务
  const handleTaskSelect = (task: Task) => {
    // 滚动到对应的任务卡片
    const taskElement = document.querySelector(`[data-task-id="${task.id}"]`);
    if (taskElement) {
      taskElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
      // 添加高亮效果
      taskElement.classList.add('search-highlight');
      setTimeout(() => {
        taskElement.classList.remove('search-highlight');
      }, 2000);
    }
    
    onTaskSelect?.(task);
    onClose();
  };

  // 高亮搜索词
  const highlightText = (text: string, searchTerm: string) => {
    if (!text || !searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-gray-900 font-medium">
          {part}
        </mark>
      ) : part
    );
  };

  // 获取状态显示文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'TODO':
        return '待办';
      case 'IN_PROGRESS':
        return '进行中';
      case 'DONE':
        return '已完成';
      default:
        return status;
    }
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO':
        return 'text-gray-600 bg-gray-100';
      case 'IN_PROGRESS':
        return 'text-blue-600 bg-blue-100';
      case 'DONE':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // 自动聚焦输入框
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // 滚动到选中的结果
  useEffect(() => {
    const selectedResult = resultRefs.current[selectedIndex];
    if (selectedResult) {
      selectedResult.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-end justify-center pb-20 z-50 search-modal pointer-events-none">
      <div className="bg-white rounded-t-lg shadow-2xl w-full max-w-xl mx-4 max-h-80 flex flex-col border border-gray-200 pointer-events-auto">
        {/* 搜索输入框 */}
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              className="w-full pl-9 pr-4 py-2 text-sm border-none bg-gray-50 rounded-md focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none"
              placeholder="搜索任务..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          {/* 快捷键提示 */}
          <div className="mt-1.5 text-xs text-gray-400 flex items-center gap-3">
            <span>↑↓ 导航</span>
            <span>Enter 选择</span>
            <span>Esc 关闭</span>
          </div>
        </div>

        {/* 搜索结果 */}
        <div className="flex-1 overflow-y-auto">
          {searchTerm.trim() && filteredTasks.length === 0 && (
            <div className="p-6 text-center text-gray-500">
              <div className="mb-2">
                <svg className="h-8 w-8 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm">未找到匹配的任务</p>
            </div>
          )}
          
          {!searchTerm.trim() && (
            <div className="p-6 text-center text-gray-400">
              <div className="mb-2">
                <svg className="h-8 w-8 mx-auto text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-sm">开始输入以搜索任务</p>
            </div>
          )}

          {filteredTasks.map((task, index) => {
            const taskType = getTaskType(task.taskTypeId);
            return (
              <div
                key={task.id}
                ref={el => resultRefs.current[index] = el}
                className={`
                  px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors duration-150 last:border-b-0
                  ${index === selectedIndex ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-25'}
                `}
                onClick={() => handleTaskSelect(task)}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-gray-900 text-sm flex-1 mr-2 truncate">
                    {highlightText(task.title, searchTerm)}
                  </h3>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(task.status)}`}>
                      {getStatusText(task.status)}
                    </span>
                    {taskType && (
                      <span 
                        className="px-1.5 py-0.5 rounded text-xs font-medium"
                        style={{ 
                          backgroundColor: taskType.color + '15', 
                          color: taskType.color 
                        }}
                      >
                        {highlightText(taskType.name, searchTerm)}
                      </span>
                    )}
                  </div>
                </div>
                
                {task.description && (
                  <p className="text-gray-600 text-xs mb-1.5 line-clamp-1">
                    {highlightText(task.description, searchTerm)}
                  </p>
                )}
                
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {task.user && (
                    <span>{highlightText(task.user.username, searchTerm)}</span>
                  )}
                  {task.dueDate && (
                    <span>{new Date(task.dueDate).toLocaleDateString('zh-CN')}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};